// useTTS — text-to-speech playback hook
//
// Primary:  ElevenLabs API via /api/tts proxy
// Fallback: browser Web Speech Synthesis (free, always available)
//
// ElevenLabs errors (quota exceeded, 503 no key, etc.) are caught
// automatically and the browser voice takes over — silent degradation.
//
// Usage:
//   const tts = useTTS();
//   await tts.play('Hello world');    // resolves when audio ends
//   tts.stop();                        // cancel immediately
//   tts.isSpeaking                     // boolean reactive state
//   tts.muted / tts.setMuted           // mute toggle
//
// Exports:
//   unlockAudioContext()  — call inside any click handler to pre-unlock audio

import { useRef, useState, useCallback } from 'react';

// ── Shared AudioContext ───────────────────────────────────────────────────────
let _ctx = null;
function getAudioContext() {
  if (_ctx) return _ctx;
  const AC = window.AudioContext || window.webkitAudioContext;
  if (!AC) return null;
  _ctx = new AC();
  return _ctx;
}

export function unlockAudioContext() {
  const ctx = getAudioContext();
  if (ctx && ctx.state === 'suspended') {
    ctx.resume().catch(() => {});
  }
}

// Returns true if audio has already been unlocked by a prior user gesture.
// Does NOT create a context (so calling this on mount is safe).
export function isAudioUnlocked() {
  return !!_ctx && _ctx.state === 'running';
}

// ── Browser Web Speech Synthesis fallback ────────────────────────────────────
// Used automatically when ElevenLabs is unavailable/over quota.

function browserSpeak(text, genRef, gen) {
  return new Promise((resolve) => {
    if (typeof window === 'undefined' || !window.speechSynthesis) {
      resolve(); return;
    }

    window.speechSynthesis.cancel();
    const utterance = new SpeechSynthesisUtterance(text);

    // Spec: slightly slower than normal, warm, gender-neutral
    utterance.rate  = 0.88;
    utterance.pitch = 0.95;
    utterance.volume = 1.0;

    // Pick the best available voice: prefer Google Natural > any English
    const pickVoice = () => {
      const voices = window.speechSynthesis.getVoices();
      if (!voices.length) return null;
      return (
        voices.find(v => /google.*natural/i.test(v.name)) ||
        voices.find(v => /google/i.test(v.name) && /en/i.test(v.lang)) ||
        voices.find(v => /en[-_]US/i.test(v.lang)) ||
        voices.find(v => /en/i.test(v.lang)) ||
        voices[0]
      );
    };

    const trySpeak = () => {
      if (gen !== genRef.current) { resolve(); return; } // cancelled
      const voice = pickVoice();
      if (voice) utterance.voice = voice;
      utterance.onend   = resolve;
      utterance.onerror = resolve;
      window.speechSynthesis.speak(utterance);
    };

    // Voices may not be loaded yet on first call
    const voices = window.speechSynthesis.getVoices();
    if (voices.length > 0) {
      trySpeak();
    } else {
      window.speechSynthesis.onvoiceschanged = () => {
        window.speechSynthesis.onvoiceschanged = null;
        trySpeak();
      };
      // Safety timeout if onvoiceschanged never fires
      setTimeout(trySpeak, 500);
    }
  });
}

// ── Strip markdown before speaking ───────────────────────────────────────────
function fixPronunciation(text) {
  return text.replace(/\bresume\b/gi, (m) => m[0] === 'R' ? 'Resumé' : 'resumé');
}

function stripMarkdown(text) {
  return text
    .replace(/\*\*(.+?)\*\*/g, '$1')   // **bold**
    .replace(/\*(.+?)\*/g, '$1')        // *italic*
    .replace(/__(.+?)__/g, '$1')        // __bold__
    .replace(/_(.+?)_/g, '$1')          // _italic_
    .replace(/`(.+?)`/g, '$1')          // `code`
    .replace(/#{1,6}\s+/g, '')          // # headings
    .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1') // [link](url)
    .replace(/!\[([^\]]*)\]\([^)]+\)/g, '$1') // ![img](url)
    .replace(/^\s*[-*+]\s+/gm, '')      // bullet points
    .replace(/^\s*\d+\.\s+/gm, '')      // numbered lists
    .replace(/~~/g, '')                 // ~~strikethrough~~
    .replace(/\s{2,}/g, ' ')            // collapse extra spaces
    .trim();
}

// ── Hook ─────────────────────────────────────────────────────────────────────
export function useTTS() {
  const sourceRef  = useRef(null);   // AudioContext BufferSourceNode
  const uttRef     = useRef(null);   // SpeechSynthesisUtterance (fallback)
  const resolveRef = useRef(null);
  const genRef     = useRef(0);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [muted,      setMuted]      = useState(false);

  // ── stop ───────────────────────────────────────────────────────────────────
  const stop = useCallback(() => {
    genRef.current++;
    // Stop AudioContext source
    if (sourceRef.current) {
      try { sourceRef.current.onended = null; sourceRef.current.stop(); } catch {}
      sourceRef.current = null;
    }
    // Stop browser speech
    if (uttRef.current) {
      try { window.speechSynthesis?.cancel(); } catch {}
      uttRef.current = null;
    }
    if (resolveRef.current) {
      resolveRef.current();
      resolveRef.current = null;
    }
    setIsSpeaking(false);
  }, []);

  // ── play ───────────────────────────────────────────────────────────────────
  const play = useCallback(async (rawText, voiceId) => {
    stop();
    if (muted) throw new Error('TTS muted');
    const text = fixPronunciation(stripMarkdown(rawText));

    const gen = genRef.current;
    setIsSpeaking(true);

    // ── Attempt 1: ElevenLabs via Web Audio API ───────────────────────────
    let elevenlabsFailed = false;
    try {
      const res = await fetch('/api/tts', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text, ...(voiceId ? { voiceId } : {}) }),
      });
      if (gen !== genRef.current) { setIsSpeaking(false); return; }

      if (!res.ok) {
        let detail = '';
        try {
          const j = await res.json();
          const d = j.detail;
          detail = typeof d === 'string' ? d : JSON.stringify(d);
        } catch {}
        // Quota exceeded or no key — warn and fall through to browser TTS
        if (res.status === 401 || res.status === 503 || res.status === 502) {
          console.warn(`[TTS] ElevenLabs unavailable (${res.status}) — using browser voice. ${detail}`);
          elevenlabsFailed = true;
        } else {
          console.warn(`[TTS] API returned ${res.status} — using browser voice.`);
          elevenlabsFailed = true;
        }
      } else {
        const arrayBuffer = await res.arrayBuffer();
        if (gen !== genRef.current) { setIsSpeaking(false); return; }

        const ctx = getAudioContext();
        if (ctx) {
          if (ctx.state === 'suspended') {
            try { await ctx.resume(); } catch {}
          }
          if (gen !== genRef.current) { setIsSpeaking(false); return; }

          const decoded = await ctx.decodeAudioData(arrayBuffer);
          if (gen !== genRef.current) { setIsSpeaking(false); return; }

          await new Promise((resolve) => {
            resolveRef.current = resolve;
            const source = ctx.createBufferSource();
            source.buffer = decoded;
            source.connect(ctx.destination);
            sourceRef.current = source;

            // Safety timeout: Chrome starts AudioContext suspended on page load
            // (no user gesture). source.start() on a suspended context schedules
            // audio silently but onended never fires — causing an infinite hang.
            // After the expected audio duration + 3s, stop the source and move on.
            const safetyMs = Math.ceil(decoded.duration * 1000) + 3000;
            const safetyId = setTimeout(() => {
              if (gen !== genRef.current) return; // stop() already called
              if (sourceRef.current) {
                try { source.stop(); } catch {}
                sourceRef.current = null;
              }
              resolveRef.current = null;
              if (gen === genRef.current) setIsSpeaking(false);
              resolve();
            }, safetyMs);

            source.onended = () => {
              clearTimeout(safetyId);
              sourceRef.current  = null;
              resolveRef.current = null;
              if (gen === genRef.current) setIsSpeaking(false);
              resolve();
            };
            try { source.start(0); } catch {
              clearTimeout(safetyId);
              sourceRef.current  = null;
              resolveRef.current = null;
              setIsSpeaking(false);
              resolve();
            }
          });
          return; // ElevenLabs succeeded
        } else {
          elevenlabsFailed = true; // No AudioContext — fall through
        }
      }
    } catch (err) {
      if (gen !== genRef.current) { setIsSpeaking(false); return; }
      console.warn('[TTS] ElevenLabs error, using browser voice:', err.message);
      elevenlabsFailed = true;
    }

    // ── Attempt 2: Browser Web Speech Synthesis ───────────────────────────
    if (elevenlabsFailed && gen === genRef.current) {
      await browserSpeak(text, genRef, gen);
    }

    if (gen === genRef.current) setIsSpeaking(false);
  }, [stop, muted]);

  return { play, stop, isSpeaking, muted, setMuted };
}
